<script setup>
import { IonRange } from "@ionic/vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useListenConfigStore } from "@/hooks/useListenConfigStore";
import { imageConfig } from "@/views/listening/components/ThemeTag/imageConfig";

// Stores & Refs
const listenStore = useListenConfigStore();
const showVolume = ref(false);
const volumeBoxRef = ref();
const rangeValue = ref(listenStore.volume);

// Computed
const currentVolume = computed(() => listenStore.volume);
const theme = computed(() => listenStore.theme);
const imageMap = computed(() => imageConfig[theme.value]);

// Methods
const toggleVolumePanel = () => {
  showVolume.value = !showVolume.value;
};

const handleVolumeChange = (event) => {
  const newVolume = event.detail.value;
  rangeValue.value = newVolume;
  listenStore.setVolume(newVolume);
};

const handleClickOutside = (e) => {
  if(showVolume.value){
    let isSelf = volumeBoxRef.value.contains(e.target)
    if(!isSelf && e.target.alt !== 'volume'){
      showVolume.value = false
    }
  }
};

// Watchers
watch(currentVolume, (newVolume) => {
  rangeValue.value = newVolume;
});

// Lifecycle
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div class="volume-container">
    <img
      :src="imageMap.icon_volume"
      alt="volume"
      class="volume-trigger"
      @click="toggleVolumePanel"
    />

    <transition name="volume-panel">
      <div v-if="showVolume" class="volume-panel" ref="volumeBoxRef">
        <ion-range
          class="volume-range"
          :value="rangeValue"
          :pin="true"
          :pin-formatter="(value) => `${value}%`"
          @ionKnobMoveEnd="handleVolumeChange"
        >
          <img
            slot="start"
            class="volume-icon"
            src="@/assets/listening/icon-volume-dark.svg"
            alt=""
          />
        </ion-range>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="less">
.volume-container {
  position: relative;
  display: inline-block;
}

.volume-trigger {
  width: 1.3rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.volume-panel {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 0.5rem;
  width: 10rem;
  height: 2rem;
  background: #fff;
  border-radius: 2rem;
  padding: 0 0.6rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  align-items: center;

  .volume-range {
    width: 100%;
    padding: 0;
    --bar-height: 0.2rem;
    --height: 2rem;
    --knob-background: var(--accent-active-color);
    --bar-background-active: var(--accent-active-color);
    --pin-background: var(--accent-active-color);
    --pin-color: #fff;

    .volume-icon {
      width: 1rem;
      height: 1rem;
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.1);
      }
    }
  }
}

// Transition animations
.volume-panel-enter-active,
.volume-panel-leave-active {
  transition: all 0.2s ease;
  transform-origin: top center;
}

.volume-panel-enter-from {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}

.volume-panel-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.95);
}

// Responsive design
@media (max-width: 480px) {
  .volume-panel {
    width: 9rem;
    padding: 0 0.4rem;
  }
}
</style>
